import { PencilLine, Trash } from "@phosphor-icons/react";
import { AssessmentRegion } from "../manage-asesmen";
import { useState } from "react";
import BaseModal from "../../../components/modal/base";
import { Button } from "../../../components/buttons";
import { Spinner } from "flowbite-react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormInput } from "../../../components/forms/input";
import { FormSelectAsync } from "../../../components/forms/input-select";
import { request } from "../../../api/config";
import { City } from "../../../types/common";
import { FormTextArea } from "../../../components/forms/input-textarea";
import { deleteRegion, updateRegion } from "../manage-asesmen.service";
import { useAlert } from "../../../stores/alert";
import { useManageAsesmenStore } from "../manage-asesmen.store";
import ModalDeleteConfirmation from "../../../components/modal/delete-confirmation";

const createRegionalPayload = z.object({
  city_ids: z
    .array(
      z.object({
        name: z.string(),
        id: z.union([z.string(), z.number()]),
      })
    )
    .min(1, "At least one city ID is required"),
  name: z.string().min(1, "Name is required"),
  notes: z.string().optional(),
  description: z.string().optional(),
});

type CreateRegionalScheme = z.infer<typeof createRegionalPayload>;

type Props = {
  item: AssessmentRegion;
};

type ErrorForm = {
  city_ids: [] | null;
  name: [] | null;
  notes: [] | null;
  description: [] | null;
};

const RegionalItem = ({ item }: Props) => {
  const [modalEdit, setModalEdit] = useState<boolean>(false);
  const [modalDelete, setModalDelete] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingDelete, setLoadingDelete] = useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorForm | undefined>(undefined);

  const { setMessage } = useAlert();
  const { getRegions } = useManageAsesmenStore();

  const selectCities = async (inputValue: string) => {
    let params = {
      q: inputValue,
    };
    const { data } = await request.get("/select/cities", {
      params: params,
    });
    return data.data;
  };

  const {
    control,
    handleSubmit,
    // formState: { errors },
  } = useForm<CreateRegionalScheme>({
    resolver: zodResolver(createRegionalPayload),
    defaultValues: {
      city_ids: item.cities.map((item) => ({
        name: item.city.name,
        id: item.city.id,
      })),
      name: item.name,
      notes: item.notes ?? "",
      description: item.description ?? "",
    },
  });

  const handleUpdate = handleSubmit(async (data) => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        city_ids: data.city_ids.map((item) => item.id),
      };
      await updateRegion(item.id, payload);
      setModalEdit(false);
      setMessage("Berhasil menyimpan data", "success");
      getRegions();
    } catch (err: any) {
      setErrors(err?.response?.data?.errors);
    } finally {
      setLoading(false);
    }
  });

  const handleDelete = async () => {
    setLoadingDelete(true);
    try {
      await deleteRegion(item.id);
      setModalDelete(false);
      getRegions();
    } catch (error) {
      setMessage("Oops, something went wrong", "error");
    } finally {
      setLoadingDelete(false);
    }
  };

  return (
    <>
      <div className="bg-gray-50 border border-gray-100 p-3 rounded-lg relative mb-2">
        <div className="absolute top-0 right-0 p-3">
          <div className="flex gap-3">
            <button
              className="text-blue-600 hover:text-blue-700"
              onClick={() => setModalEdit(true)}
            >
              <PencilLine size={18} />
            </button>
            <button
              className="text-red-600 hover:text-red-700"
              onClick={() => setModalDelete(true)}
            >
              <Trash size={18} />
            </button>
          </div>
        </div>
        <span className="block text-lg font-semibold">{item.name ?? ""}</span>
        <small className="block leading-tight mt-1">
          {item.cities.map((item) => item.city.name).join(", ")}
        </small>
      </div>

      <BaseModal
        title={`Ubah ${item.name}`}
        isOpen={modalEdit}
        close={() => setModalEdit(false)}
        size="2xl"
      >
        <div className="py-4">
          <FormInput
            label="Nama Regional"
            required
            control={control}
            name="name"
          />
          <FormSelectAsync
            label="Pilih Kota"
            name="city_ids"
            control={control}
            loadOption={selectCities}
            multiple={true}
            required
            optionLabel={(option: City) => `${option.name}`}
            optionValue={(option: City) => `${option.id}`}
            error={errors?.city_ids}
          />
          <FormTextArea
            label="Deskripsi"
            control={control}
            name="description"
          />
        </div>
        <div className="mt-4 flex items-center justify-end">
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setModalEdit(false)}
              variant="danger"
              className="px-4"
            >
              Batal
            </Button>
            <Button onClick={handleUpdate} className="px-10">
              {loading ? <Spinner /> : "Iya!"}
            </Button>
          </div>
        </div>
      </BaseModal>

      <ModalDeleteConfirmation
        isOpen={modalDelete}
        close={() => setModalDelete(false)}
        subTitle="modul"
        name={item?.name}
        loading={loadingDelete}
        action={handleDelete}
      />
    </>
  );
};

export default RegionalItem;
